<template>
	<div class="multi-select" :class="`ms-${type} ms-icon-${iconSize} ${hideLabel ? 'hide-label' : ''}`">
		<div v-for="(category, catIndex) in categories" :key="category.name"
			class="multi-select-category"
		>
			<foldable-container
				:show-header="showCatName"
				:header-background="category.selected ? 'var(--light-blue)' : undefined"
				@fold-state-changed="!$event && foldEachExcepted(catIndex)"
				ref="foldableContainers"
			>
				<template #header>
					<div  class="ms-category-header">
						<span class="ms-category-name">{{ category.name }}</span>
					</div>
				</template>
				<template #content>
					<div class="ms-option-list" :class="{ inlineOptions }">
						<div v-for="(option) in category.options" :key="option.key"
							@click.stop="validateInput(option.key, category)"
							class="multi-select-option hl-hover"
							:class="{ selected: optionStates[option.key] }"
							ref="options"
						>
							<img v-if="option.icon" v-lazy="iconURL(option.icon)" alt="">
							<span class="ms-option-name">
								{{ option.name ?? option.key }}
							</span>
						</div>
					</div>
				</template>
			</foldable-container>
		</div>
	</div>
</template>

<script>
import FoldableContainer from '../UI/FoldableContainer.vue';
import {
	isPlainObject as _isPlainObject
}	from 'lodash';

export default {
	name: 'multi-select',
	props: {
		iconSize: {
			type: String,
			default: 'md'
		},
		type: {
			type: String,
			default: 'grid' // options: grid | list
		},
		hideLabel: {
			type: Boolean,
			default: false
		},
		modelValue: {
			type: Array,
			required: true
		},
		options: {
			type: Array,
			required: true,
			validator(options) {
				for (const o of options) {
					if (!_isPlainObject(o) || !('key' in o))
						return false;
				}
				return true;
			}
		}
	},
	components: {
		FoldableContainer
	},
	data() {
		return {
			categories: [],
			optionStates: this.options.reduce((ag, o) => {
				ag[o.key] = this.modelValue.includes(o.key);
				return ag;
			}, {})
		};
	},
	computed: {
		showCatName() {
			return this.categories.length > 1
				|| this.categories[0]?.name !== '__none';
		},
		inlineOptions() {
			return !this.showCatName && this.options.length <= 4;
		}
	},
	watch: {
		type() {
			this.$nextTick(() => {
				this.updateContainers();
			});
		},
		options: {
			immediate: true,
			handler(newOptions) {
				const byCategoriesObj = newOptions.reduce((cats, option) => {
					const category = option.category ?? '__none';
					if (!cats[category])
						cats[category] = [];
					cats[category].push(option);
					return cats;
				}, {});
				this.categories = Object.entries(byCategoriesObj)
					.map(([ catName, optionList ]) => {
						return {
							name: catName,
							selected: 0,
							options: optionList.sort((a, b) => {
								return (a.tags ?? '').localeCompare(b.tags ?? '');
							})
						};
					}).sort((a, b) => {
						return a.name.localeCompare(b.name);
					});
				this.updateContainers();
			}
		},
		modelValue(nv) {
			for (const cat of this.categories)
				cat.selected = 0;
			this.optionStates = this.options.reduce((ag, o) => {
				ag[o.key] = nv.includes(o.key);
				const cat = this.categories.find(c => c.options.includes(o));
				if (cat && ag[o.key])
					cat.selected++;
				return ag;
			}, {});
		}
	},
	methods: {
		foldEachExcepted(catIndex) {
			for (let i = 0; i < this.$refs.foldableContainers.length; i++) {
				if (i === catIndex)
					continue;
				this.$refs.foldableContainers[i].fold();
			}
			setTimeout(() => {
				this.$refs.foldableContainers[catIndex].$el.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
					inline: 'nearest'
				});
			}, 150);
		},
		validateInput(optionIndex, category) {
			this.optionStates[optionIndex] = !this.optionStates[optionIndex];
			category.selected += 1 - 2 * ~~(!this.optionStates[optionIndex]);
			this.$emit('update:modelValue', this.options.filter((o) => this.optionStates[o.key]).map(o => o.key));
		},
		iconURL(pathToIcon) {
			return `https://static.wikia.nocookie.net/rockman_x_dive/images${pathToIcon}`;
		},
		updateContainers() {
			this.$nextTick(() => {
				for (const cpnt of (this.$refs?.foldableContainers ?? []))
					cpnt.refreshHeight();
			});
		}
	}
};
</script>

<style lang="scss">
.multi-select {
	width: 100%;
	user-select: none;
	border-radius: .25rem;
	.multi-select-category {
		margin-bottom: 5px;
		.ms-category-header {
			text-align: center;
			padding: 5px 0;
			.ms-category-name {
				font-weight: bold;
				text-transform: uppercase;
			}
		}
		.ms-option-list {
			.multi-select-option {
				padding: .25rem .5rem;
				display: flex;
				align-items: center;
				cursor: pointer;
				border-radius: 5px;
				>img {
					width: 50px;
					height: 50px;
				}
				.ms-option-name {
					padding-left: 5px;
				}
				&.selected {
					background: var(--light-blue);
				}
			}
		}
	}
	&.hide-label {
		.ms-option-name {
			display: none;
		}
	}
	&.ms-grid {
		.multi-select-category .ms-option-list {
			&:not(.inlineOptions) {
				display: grid;
				grid-column-gap: .25rem;
				grid-row-gap: .25rem;
				justify-items: center;
				grid-template-columns: 1fr 1fr;
				.multi-select-option {
					justify-content: start;
					width: 100%;
				}
			}
			&.inlineOptions  {
				display: flex;
				.multi-select-option {
					justify-content: center;
					width: 100%;
				}
			}
		}
	}
	&.ms-icon-md {
		.ms-option-list .multi-select-option > img {
			width: 50px;
			height: 50px;
			background: #0004;
			border-radius: 5px;
		}
	}
	&.ms-icon-sm {
		.ms-option-list .multi-select-option > img {
			width: 25px;
			height: 25px;
		}
	}
}
</style>
